import { admitApplicationCompositionInputs } from "./admit-application-composition"
import type {
  CompileCompositionResult,
  ProjectValidationCompositionAuthority,
} from "./types"

export function compileApplicationComposition(
  candidatePlan: unknown,
  candidateRelease: unknown,
  projectValidationAuthority?: ProjectValidationCompositionAuthority,
): CompileCompositionResult {
  const admission = admitApplicationCompositionInputs(
    candidatePlan,
    candidateRelease,
    projectValidationAuthority,
  )
  if (!admission.ok) return admission
  const { plan, release } = admission.input

  const matchingRecipes = release.recipes.filter(
    (candidate) => candidate.recipeId === plan.recipeRef,
  )
  if (matchingRecipes.length !== 1) {
    throw new Error("Quality policy accepted non-unique recipe authority.")
  }
  const recipe = matchingRecipes[0]
  const regionOrder = new Map(
    recipe.regions.map((region) => [region.regionId, region.order]),
  )
  const inputOrder = new Map(
    plan.semanticNodes.map((node, index) => [node.nodeId, index]),
  )

  const nodes = plan.semanticNodes
    .map((node) => {
      const matchingComponents = release.components.filter(
        (candidate) => candidate.semanticRole === node.semanticRole,
      )
      if (matchingComponents.length !== 1) {
        throw new Error(
          `Quality policy accepted non-unique component ${node.semanticRole}.`,
        )
      }
      const component = matchingComponents[0]

      const action = node.action
        ? (() => {
            const matchingSlots = component.actionSlots.filter(
              (candidate) => candidate.slotId === node.action?.slotRef,
            )
            if (matchingSlots.length !== 1) {
              throw new Error(
                `Quality policy accepted non-unique slot ${node.action.slotRef}.`,
              )
            }
            const slot = matchingSlots[0]
            return {
              ...node.action,
              tokenRef: release.actionTokenBindings[node.action.role],
              exclusiveGroup: slot.exclusiveGroup,
            }
          })()
        : undefined

      return {
        nodeId: node.nodeId,
        componentRef: component.componentId,
        rendererRef: component.rendererRef,
        regionRef: node.regionRef,
        content: structuredClone(node.content),
        visibleIn: [...node.visibleIn],
        tokenRefs: [...component.tokenRefs],
        ...(action ? { action } : {}),
        provenance: {
          planRef: plan.planId,
          releaseRef: release.releaseId,
          recipeRef: recipe.recipeId,
        },
      }
    })
    .sort((left, right) => {
      const byRegion =
        (regionOrder.get(left.regionRef) ?? 0) -
        (regionOrder.get(right.regionRef) ?? 0)
      return byRegion !== 0
        ? byRegion
        : (inputOrder.get(left.nodeId) ?? 0) -
            (inputOrder.get(right.nodeId) ?? 0)
    })

  return {
    ok: true,
    artifact: {
      schemaVersion: "cogo.compiled-application-composition.v1",
      planId: plan.planId,
      applicationCoreRef: plan.applicationCoreRef,
      target: plan.target,
      locale: plan.locale,
      designReleaseRef: release.releaseId,
      recipeRef: recipe.recipeId,
      states: [...plan.states],
      nodes,
      generationTrace: {
        mode: "deterministic-design-composition",
        rawPresentationAuthoredByModel: false,
        genericFallbackUsed: false,
        generatedOutputAdmissionAllowed: false,
        ...(admission.input.projectValidationAuthority
          ? {
              projectValidationAuthority: structuredClone(
                admission.input.projectValidationAuthority,
              ),
            }
          : {}),
      },
    },
  }
}
